<template>
  <div class="medication-settings m-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Predefined Protocols</h5>
      <b-button
        class="pt-2 pb-2"
        size="sm"
        variant="primary"
        @click="addPredefinedProtocol"
        >New</b-button
      >
    </div>
    <grid
      ref="grid"
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="predefinedProtocols"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
    />
    <b-modal
      centered
      ref="form-modal"
      :title="(formId ? 'Edit ' : 'Add ') + 'predefined protocol'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok.prevent="submitPredefinedProtocol"
      no-close-on-backdrop
      header-class="bg-purple"
      size="xl"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-row
        ><b-col
          ><b-form-group
            label="Name"
            :state="formErrors.name.length == 0 ? null : false"
            :invalid-feedback="formErrors.name.join(', ')"
          >
            <b-form-input
              v-model="form.name"
              :state="formErrors.name.length == 0 ? null : false"
              maxLength="100"
            ></b-form-input> </b-form-group></b-col
      ></b-row>

      <b-row>
        <b-col>
          <b-form-group label="Scheduled Medications">
            <template v-slot:label="">
              <div class="d-flex justify-content-between">
                <div>Scheduled Medications</div>
                <div>
                  <b-button
                    class="pt-2 pb-2"
                    size="sm"
                    variant="primary"
                    @click="addScheduledMedication"
                    >New Scheduled Medication</b-button
                  >
                </div>
              </div>
            </template>
            <grid
              ref="gridScheduled"
              :groupFeature="false"
              :columns="scheduledColumns"
              :autoHeight="true"
              :data="scheduledItems"
              :columnLines="false"
              :searchFeature="true"
              :stripeFeature="false"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-form-group label="Prn Medications">
            <template v-slot:label="">
              <div class="d-flex justify-content-between">
                <div>Prn Medications</div>
                <div>
                  <b-button
                    class="pt-2 pb-2"
                    size="sm"
                    variant="primary"
                    @click="addPrnMedication"
                    >New Prn Medication</b-button
                  >
                </div>
              </div>
            </template>
            <grid
              ref="gridPrn"
              :groupFeature="false"
              :columns="prnColumns"
              :autoHeight="true"
              :data="prnItems"
              :columnLines="false"
              :searchFeature="true"
              :stripeFeature="false"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
    <b-modal
      centered
      ref="form-scheduled-modal"
      :title="(formScheduledId ? 'Edit ' : 'Add ') + 'scheduled medication'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok.prevent="submitScheduledMedication"
      no-close-on-backdrop
      header-class="bg-purple"
      size="lg"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-row>
        <b-col>
          <b-form-group
            label="Medication"
            :state="formScheduledErrors.medication.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.medication.join(', ')"
          >
            <v-select
              placeholder="Search for medications"
              v-model="formScheduled.medication"
              @search="searchMedications"
              :options="medications"
              :class="medications.length == 0 ? 'picker-hide-options' : ''"
            >
              <template #open-indicator> <span></span> </template>
            </v-select>
          </b-form-group>
          <b-form-group
            label="Route"
            :state="formScheduledErrors.route.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.route.join(', ')"
          >
            <v-select
              v-model="formScheduled.route"
              :options="medicationRoutes"
            ></v-select>
          </b-form-group>
          <b-row>
            <b-col>
              <b-form-group
                label="Dosage"
                :state="formScheduledErrors.dosage.length == 0 ? null : false"
                :invalid-feedback="formScheduledErrors.dosage.join(', ')"
              >
                <b-form-input
                  v-model="formScheduled.dosage"
                  type="number"
                  :state="formScheduledErrors.dosage.length == 0 ? null : false"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Unit of measurement"
                :state="
                  formScheduledErrors.measurementUnit.length == 0 ? null : false
                "
                :invalid-feedback="
                  formScheduledErrors.measurementUnit.join(', ')
                "
              >
                <v-select
                  v-model="formScheduled.measurementUnit"
                  :options="measurements"
                ></v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-form-group
            label="Start Day"
            :state="formScheduledErrors.fromDay.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.fromDay.join(', ')"
          >
            <b-form-input
              v-model.number="formScheduled.fromDay"
              :state="formScheduledErrors.fromDay.length == 0 ? null : false"
              type="number"
              min="1"
            ></b-form-input>
          </b-form-group>
          <b-form-checkbox v-model="formScheduled.untilDischarge">
            Until Discharge
          </b-form-checkbox>
          <b-form-group
            label="End Day"
            :state="formScheduledErrors.toDay.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.toDay.join(', ')"
            :disabled="formScheduled.untilDischarge"
          >
            <b-form-input
              v-model.number="formScheduled.toDay"
              :state="formScheduledErrors.toDay.length == 0 ? null : false"
              type="number"
              min="1"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Frequency"
            :state="formScheduledErrors.frequencyId.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.frequencyId.join(', ')"
          >
            <v-select
              v-model="scheduledFrequency"
              :options="frequencyOptions"
            ></v-select>
          </b-form-group>
          <b-form-group
            v-if="formScheduled.frequencyId == 'daysOfWeek'"
            :state="formScheduledErrors.weekDays.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.weekDays.join(', ')"
          >
            <week-days-picker v-model="formScheduled.weekDays" />
          </b-form-group>
          <b-form-group
            v-if="formScheduled.frequencyId == 'daysInterval'"
            label="Days interval"
            :state="
              formScheduledErrors.daysIntervalId.length == 0 ? null : false
            "
            :invalid-feedback="formScheduledErrors.daysIntervalId.join(', ')"
          >
            <v-select
              v-model="daysInterval"
              :options="daysIntervalOptions"
            ></v-select>
          </b-form-group>
          <b-form-group
            label="Times per day"
            :state="
              formScheduledErrors.timesPerDayId.length == 0 ? null : false
            "
            :invalid-feedback="formScheduledErrors.timesPerDayId.join(', ')"
          >
            <v-select
              v-model="timesPerDay"
              :options="timesPerDayOptions"
            ></v-select>
          </b-form-group>
          <b-form-group
            label="Times"
            :state="formScheduledErrors.medication.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.medication.join(', ')"
          >
            <div
              class="times-container rounded border w-100 d-flex flex-wrap justify-content-start align-items-start p-2"
            >
              <div
                v-for="timeIndex in Object.keys(formScheduled.times)"
                :key="timeIndex"
                class="mr-2"
              >
                <b-form-select
                  :options="timeOptions"
                  :value="formScheduled.times[timeIndex]"
                  @input="newValue => setTime(timeIndex, newValue)"
                ></b-form-select>
              </div>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Instructions">
            <b-form-input v-model="formScheduled.instructions"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
    <b-modal
      centered
      ref="form-prn-modal"
      :title="(formPrnId ? 'Edit ' : 'Add ') + 'prn medication'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok.prevent="submitPrnMedication"
      no-close-on-backdrop
      header-class="bg-purple"
      size="lg"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-row>
        <b-col>
          <b-form-group
            label="Medication"
            :state="formPrnErrors.medication.length == 0 ? null : false"
            :invalid-feedback="formPrnErrors.medication.join(', ')"
          >
            <v-select
              placeholder="Search for medications"
              v-model="formPrn.medication"
              @search="searchMedications"
              :options="medications"
              :class="medications.length == 0 ? 'picker-hide-options' : ''"
            >
              <template #open-indicator> <span></span> </template>
            </v-select>
          </b-form-group>
          <b-form-group
            label="Route"
            :state="formPrnErrors.route.length == 0 ? null : false"
            :invalid-feedback="formPrnErrors.route.join(', ')"
          >
            <v-select
              v-model="formPrn.route"
              :options="medicationRoutes"
            ></v-select>
          </b-form-group>
          <b-row>
            <b-col>
              <b-form-group
                label="Dosage"
                :state="formPrnErrors.dosage.length == 0 ? null : false"
                :invalid-feedback="formPrnErrors.dosage.join(', ')"
              >
                <b-form-input
                  v-model="formPrn.dosage"
                  type="number"
                  :state="formPrnErrors.dosage.length == 0 ? null : false"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Unit of measurement"
                :state="
                  formPrnErrors.measurementUnit.length == 0 ? null : false
                "
                :invalid-feedback="formPrnErrors.measurementUnit.join(', ')"
              >
                <v-select
                  v-model="formPrn.measurementUnit"
                  :options="measurements"
                ></v-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col>
          <b-form-group
            label="Start Day"
            :state="formPrnErrors.fromDay.length == 0 ? null : false"
            :invalid-feedback="formPrnErrors.fromDay.join(', ')"
          >
            <b-form-input
              v-model.number="formPrn.fromDay"
              :state="formPrnErrors.fromDay.length == 0 ? null : false"
              type="number"
              min="1"
            ></b-form-input>
          </b-form-group>
          <b-form-group
            label="End Day"
            :state="formPrnErrors.toDay.length == 0 ? null : false"
            :invalid-feedback="formPrnErrors.toDay.join(', ')"
            :disabled="formPrn.untilDischarge"
          >
            <b-form-input
              v-model.number="formPrn.toDay"
              :state="formPrnErrors.toDay.length == 0 ? null : false"
              type="number"
              min="1"
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Maximum Dosage Per Day">
            <b-form-input v-model="formPrn.maximumDosagePerDay"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Instructions">
            <b-form-input v-model="formPrn.instructions"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import predefinedProtocolServer from "../../server/predefinedProtocol";
import { mapActions, mapState } from "vuex";
import { tryParseInt } from "../../helpers/general";
import { validateRequired } from "../../helpers/validation";
import medicationServer from "../../server/medication";
import {
  frequencies,
  daysIntervals,
  timesPerDayOptions,
  timeOptions
} from "../../config/medications";
import { formatTimeOnly } from "../../helpers/date";
import WeekDaysPicker from "../controls/WeekDaysPicker";
export default {
  name: "PredefinedProtocolCrud",
  components: {
    Grid,
    WeekDaysPicker
  },
  data() {
    return {
      medications: [],
      formId: null,
      form: {
        name: "",
        scheduledMedications: [],
        prnMedications: []
      },
      formErrors: {
        name: [],
        scheduledMedications: [],
        prnMedications: []
      },
      formScheduledId: null,
      formScheduled: {
        medication: null,
        route: null,
        dosage: "",
        measurementUnit: null,
        fromDay: null,
        toDay: null,
        untilDischarge: false,
        frequencyId: null,
        daysIntervalId: null,
        weekDays: [],
        timesPerDayId: null,
        times: [],
        instructions: ""
      },
      formScheduledErrors: {
        medication: [],
        route: [],
        dosage: [],
        measurementUnit: [],
        fromDay: [],
        toDay: [],
        frequencyId: [],
        daysIntervalId: [],
        weekDays: [],
        timesPerDayId: []
      },
      formPrnId: null,
      formPrn: {
        medication: null,
        route: null,
        dosage: "",
        measurementUnit: null,
        fromDay: null,
        toDay: null,
        instructions: "",
        maximumDosagePerDay: ""
      },
      formPrnErrors: {
        medication: [],
        route: [],
        dosage: [],
        measurementUnit: [],
        fromDay: [],
        toDay: []
      }
    };
  },
  mounted() {
    this.fetchPredefinedProtocols();
    this.fetchMedicationRoutes();
    this.fetchMeasurements();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", [
      "fetchPredefinedProtocols",
      "fetchMedicationRoutes",
      "fetchMeasurements"
    ]),
    validatePredefinedProtocolSubmit() {
      let withoutErrors = true;

      this.formErrors.name = [];
      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitPredefinedProtocol() {
      const result = this.validatePredefinedProtocolSubmit();
      if (result) {
        const formData = {
          name: this.form.name,
          medications: this.form.scheduledMedications.map(
            scheduledMedication => {
              return {
                medicationId: scheduledMedication.medication.value,
                dosageAmount: parseFloat(scheduledMedication.dosage),
                unitOfMeasurementId: scheduledMedication.measurementUnit.value,
                medicationRouteId: scheduledMedication.route.value,
                instructions: scheduledMedication.instructions,
                fromDay: parseInt(scheduledMedication.fromDay),
                toDay: tryParseInt(scheduledMedication.toDay, null),
                untilDischarge: scheduledMedication.untilDischarge,
                frequency:
                  scheduledMedication.frequencyId.charAt(0).toUpperCase() +
                  scheduledMedication.frequencyId.slice(1),
                interval: this.toIntervalAmounts(scheduledMedication).frequency,
                intervalAmount: this.toIntervalAmounts(scheduledMedication)
                  .amount,
                timesPerDay: Object.values(scheduledMedication.times).length,
                monday: scheduledMedication.weekDays.indexOf("1") >= 0,
                tuesday: scheduledMedication.weekDays.indexOf("2") >= 0,
                wednesday: scheduledMedication.weekDays.indexOf("3") >= 0,
                thursday: scheduledMedication.weekDays.indexOf("4") >= 0,
                friday: scheduledMedication.weekDays.indexOf("5") >= 0,
                saturday: scheduledMedication.weekDays.indexOf("6") >= 0,
                sunday: scheduledMedication.weekDays.indexOf("0") >= 0,
                times: Object.values(scheduledMedication.times)
              };
            }
          ),
          prnMedications: this.form.prnMedications.map(prnMedication => {
            return {
              medicationId: prnMedication.medication.value,
              dosageAmount: parseFloat(prnMedication.dosage),
              unitOfMeasurementId: prnMedication.measurementUnit.value,
              medicationRouteId: prnMedication.route.value,
              instructions: prnMedication.instructions,
              maximumDosagePerDay: prnMedication.maximumDosagePerDay,
              fromDay: tryParseInt(prnMedication.fromDay, null),
              toDay: tryParseInt(prnMedication.toDay, null)
            };
          })
        };
        if (this.formId) {
          await predefinedProtocolServer.update(this.formId, formData);
        } else {
          await predefinedProtocolServer.create(formData);
        }
        this.fetchPredefinedProtocols();
        this.showAlertSuccess();
        this.$refs["form-modal"].hide();
      }
    },
    addPredefinedProtocol() {
      this.formId = null;
      this.form = {
        name: "",
        scheduledMedications: [],
        prnMedications: []
      };
      this.formErrors = {
        name: [],
        scheduledMedications: [],
        prnMedications: []
      };
      this.$refs["form-modal"].show();
    },
    async searchMedications(search, loading) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      if (search && search.trim().length >= 3) {
        this.medications = [];
      }

      this.lastTimeout = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const medications = await medicationServer.query(
            { searchPhrase: search, page: 0, pageSize: 10, orderBy: "name" },
            { "Hide-Loading": true }
          );
          this.medications = medications.map(m => ({
            value: m.id,
            label: m.name
          }));
        } else {
          this.medications = [];
        }
        loading(false);
      }, 1000);
    },
    clearErrors() {
      this.formScheduledErrors = {
        medication: [],
        route: [],
        dosage: [],
        measurementUnit: [],
        fromDay: [],
        toDay: [],
        frequencyId: [],
        daysIntervalId: [],
        weekDays: [],
        timesPerDayId: []
      };
      this.formPrnErrors = {
        medication: [],
        route: [],
        dosage: [],
        measurementUnit: [],
        fromDay: [],
        toDay: []
      };
    },
    addScheduledMedication() {
      this.formScheduledId = null;
      this.formScheduled = {
        medication: null,
        route: null,
        dosage: "",
        measurementUnit: null,
        fromDay: null,
        untilDischarge: false,
        toDay: null,
        frequencyId: null,
        daysIntervalId: null,
        weekDays: [],
        timesPerDayId: null,
        times: [],
        instructions: ""
      };
      this.clearErrors();
      this.$refs["form-scheduled-modal"].show();
    },
    addPrnMedication() {
      this.formPrnId = null;
      this.formPrn = {
        medication: null,
        route: null,
        dosage: "",
        measurementUnit: null,
        fromDay: null,
        toDay: null,
        instructions: "",
        maximumDosagePerDay: ""
      };
      this.clearErrors();
      this.$refs["form-prn-modal"].show();
    },
    validateScheduledSubmit() {
      let withoutErrors = true;

      this.formScheduledErrors.medication = [];
      if (!validateRequired(this.formScheduled.medication)) {
        this.formScheduledErrors.medication.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.route = [];
      if (!validateRequired(this.formScheduled.route)) {
        this.formScheduledErrors.route.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.dosage = [];
      if (!validateRequired(this.formScheduled.dosage)) {
        this.formScheduledErrors.dosage.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.measurementUnit = [];
      if (!validateRequired(this.formScheduled.measurementUnit)) {
        this.formScheduledErrors.measurementUnit.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.fromDay = [];
      if (!validateRequired(this.formScheduled.fromDay)) {
        this.formScheduledErrors.fromDay.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.toDay = [];
      if (
        !this.formScheduled.untilDischarge &&
        !validateRequired(this.formScheduled.toDay)
      ) {
        this.formScheduledErrors.toDay.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.frequencyId = [];
      if (!validateRequired(this.formScheduled.frequencyId)) {
        this.formScheduledErrors.frequencyId.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.weekDays = [];
      if (
        this.formScheduled.frequencyId == "daysOfWeek" &&
        this.formScheduled.weekDays.length == 0
      ) {
        this.formScheduledErrors.weekDays.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.daysIntervalId = [];
      if (
        this.formScheduled.frequencyId == "daysInterval" &&
        !validateRequired(this.formScheduled.daysIntervalId)
      ) {
        this.formScheduledErrors.daysIntervalId.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.timesPerDayId = [];
      if (!validateRequired(this.formScheduled.timesPerDayId)) {
        this.formScheduledErrors.timesPerDayId.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitScheduledMedication() {
      const result = await this.validateScheduledSubmit();
      if (result) {
        if (this.formScheduledId) {
          this.form.scheduledMedications.splice(
            this.formScheduled.parentIndex,
            1
          );
        }
        var index = this.form.scheduledMedications.findIndex(
          elem => elem.fromDay > this.formScheduled.fromDay
        );

        index = index == -1 ? this.form.scheduledMedications.length : index;

        this.form.scheduledMedications.splice(index, 0, {
          ...this.formScheduled,
          toDay: this.formScheduled.untilDischarge
            ? null
            : this.formScheduled.toDay
        });

        this.$refs["form-scheduled-modal"].hide();
      }
    },
    validatePrnSubmit() {
      let withoutErrors = true;

      this.formPrnErrors.medication = [];
      if (!validateRequired(this.formPrn.medication)) {
        this.formPrnErrors.medication.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.route = [];
      if (!validateRequired(this.formPrn.route)) {
        this.formPrnErrors.route.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.dosage = [];
      if (!validateRequired(this.formPrn.dosage)) {
        this.formPrnErrors.dosage.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.measurementUnit = [];
      if (!validateRequired(this.formPrn.measurementUnit)) {
        this.formPrnErrors.measurementUnit.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitPrnMedication() {
      const result = await this.validatePrnSubmit();
      if (result) {
        if (this.formPrnId) {
          this.form.prnMedications.splice(this.formPrn.parentIndex, 1);
        }
        var index = this.form.prnMedications.findIndex(
          elem => elem.fromDay > this.formPrn.fromDay
        );
        index = index == -1 ? this.form.prnMedications.length : index;
        this.form.prnMedications.splice(index, 0, {
          ...this.formPrn
        });

        this.$refs["form-prn-modal"].hide();
      }
    },
    initializeTimes() {
      if (!this.formScheduled.timesPerDayId) {
        this.formScheduled.times = [];
      } else {
        let newTimes = {};
        for (
          let index = 0;
          index <
          timesPerDayOptions[this.formScheduled.timesPerDayId].initialTimes
            .length;
          index++
        ) {
          const initialTime =
            timesPerDayOptions[this.formScheduled.timesPerDayId].initialTimes[
              index
            ];
          newTimes[index] = initialTime;
        }
        this.formScheduled.times = newTimes;
      }
    },
    setTime(timeIndex, newTime) {
      this.formScheduled.times[timeIndex] = newTime;
    },
    getDaysIntervalId(intervalLabel, intervalAmount) {
      switch (`${intervalAmount}${intervalLabel}`) {
        case "2Days":
          return "every2Days";
        case "3Days":
          return "every3Days";
        case "1Weeks":
          return "1timePerWeek";
        case "2Weeks":
          return "everyTwoWeeks";
        case "3Weeks":
          return "every3Weeks";
        case "28Days":
          return "every28Days";
        case "30Days":
          return "every30Days";

        default:
          return null;
      }
    },
    toIntervalAmounts(scheduledMedication) {
      switch (scheduledMedication.daysIntervalId) {
        case "every2Days":
          return {
            frequency: "Days",
            amount: 2
          };
        case "every3Days":
          return {
            frequency: "Days",
            amount: 3
          };
        case "1timePerWeek":
          return {
            frequency: "Weeks",
            amount: 1
          };
        case "everyTwoWeeks":
          return {
            frequency: "Weeks",
            amount: 2
          };
        case "every3Weeks":
          return {
            frequency: "Weeks",
            amount: 3
          };
        case "every28Days":
          return {
            frequency: "Days",
            amount: 28
          };
        case "every30Days":
          return {
            frequency: "Days",
            amount: 30
          };

        default:
          return {
            frequency: "Days",
            amount: 1
          };
      }
    }
  },
  computed: {
    ...mapState("client", {
      predefinedProtocols: state => state.predefinedProtocols,
      medicationRoutes: state =>
        state.medicationRoutes.map(medicationRoute => ({
          value: medicationRoute.id,
          label: medicationRoute.name
        })),
      measurements: state =>
        state.measurements.map(measurement => ({
          value: measurement.id,
          label: measurement.name
        }))
    }),
    columns() {
      let columns = [
        {
          text: "Name",
          field: "name",
          flex: 1,
          editor: false
        },
        {
          text: "",
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          align: "right",
          width: 70,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: async ({ record }) => {
                this.formId = record.id;
                this.form = {
                  name: record.data.name,
                  scheduledMedications: record.data.medications.map(
                    (medication, medicationIndex) => {
                      const times = {};
                      for (
                        let index = 0;
                        index < medication.times.length;
                        index++
                      ) {
                        const time = medication.times[index];
                        times[index] = time;
                      }
                      return {
                        medicationIndex,
                        medication: medication.medication
                          ? {
                              value: medication.medication.id,
                              label: medication.medication.name
                            }
                          : null,
                        route: medication.medicationRoute
                          ? {
                              value: medication.medicationRoute.id,
                              label: medication.medicationRoute.name
                            }
                          : null,
                        dosage: medication.dosageAmount,
                        measurementUnit: medication.unitOfMeasurement
                          ? {
                              value: medication.unitOfMeasurement.id,
                              label: medication.unitOfMeasurement.name
                            }
                          : null,
                        fromDay: medication.fromDay,
                        untilDischarge: medication.untilDischarge,
                        toDay: medication.toDay,
                        frequencyId:
                          medication.frequency.charAt(0).toLowerCase() +
                          medication.frequency.slice(1),
                        daysIntervalId: this.getDaysIntervalId(
                          medication.interval,
                          medication.intervalAmount
                        ),
                        weekDays: [
                          ...(medication.sunday ? ["0"] : []),
                          ...(medication.monday ? ["1"] : []),
                          ...(medication.tuesday ? ["2"] : []),
                          ...(medication.wednesday ? ["3"] : []),
                          ...(medication.thursday ? ["4"] : []),
                          ...(medication.friday ? ["5"] : []),
                          ...(medication.saturday ? ["6"] : [])
                        ],
                        timesPerDayId: `${medication.timesPerDay}Time${
                          medication.timesPerDay > 1 ? "s" : ""
                        }PerDay`,
                        times: times,
                        instructions: medication.instructions
                      };
                    }
                  ),
                  prnMedications: record.data.prnMedications.map(
                    (prnMedication, medicationIndex) => {
                      return {
                        medicationIndex,
                        medication: prnMedication.medication
                          ? {
                              value: prnMedication.medication.id,
                              label: prnMedication.medication.name
                            }
                          : null,
                        route: prnMedication.medicationRoute
                          ? {
                              value: prnMedication.medicationRoute.id,
                              label: prnMedication.medicationRoute.name
                            }
                          : null,
                        dosage: prnMedication.dosageAmount,
                        measurementUnit: prnMedication.unitOfMeasurement
                          ? {
                              value: prnMedication.unitOfMeasurement.id,
                              label: prnMedication.unitOfMeasurement.name
                            }
                          : null,
                        fromDay: prnMedication.fromDay,
                        toDay: prnMedication.toDay,
                        instructions: prnMedication.instructions,
                        maximumDosagePerDay: prnMedication.maximumDosagePerDay
                      };
                    }
                  )
                };
                this.formErrors = {
                  name: [],
                  scheduledMedications: [],
                  prnMedications: []
                };
                this.$refs["form-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2 mr-1" src=	"${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    await predefinedProtocolServer.delete(record.id);
                    this.fetchPredefinedProtocols();
                    this.showAlertSuccess();
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ];
      return columns;
    },
    scheduledItems() {
      return this.form.scheduledMedications.map(item => {
        let rowHeight = 50;
        const singleLineHeight = 25;
        const charsInstructionsPerLine = 80;
        const maxFactor = Math.max(
          Math.ceil(item.instructions.length / charsInstructionsPerLine)
        );
        if (maxFactor > 1) rowHeight += singleLineHeight * (maxFactor - 1);
        return {
          ...item,
          rowHeight: rowHeight,
          inputRows: maxFactor
        };
      });
    },
    scheduledColumns() {
      return [
        {
          text: "Range",
          width: 170,
          editor: false,
          type: "template",
          template: data =>
            `From: Day ${data.record.data.fromDay} - ${
              data.record.data.untilDischarge
                ? "Until Discharge"
                : "To: Day " + data.record.data.toDay
            }`
        },
        {
          text: "Medication",
          field: "medication",
          flex: 1,
          editor: false,
          type: "template",
          template: data => {
            return `<div class="text-wrap">${data.record.data.medication.label}, ${data.record.data.dosage} ${data.record.data.measurementUnit.label} by ${data.record.data.route.label}</div>`;
          },
          sortable: false
        },
        {
          text: "Times",
          field: "times",
          editor: false,
          type: "template",
          flex: 1,
          template: ({ record }) =>
            Object.values(record.data.times)
              .map(time => formatTimeOnly(time, "HH:mm"))
              .join(", "),
          sortable: false
        },
        {
          text: "Frequency",
          field: "frequency",
          editor: false,
          type: "template",
          flex: 1,
          template: ({ record }) => {
            switch (record.data.frequencyId) {
              case "everyDay":
                return "Every Day";
              case "daysInterval":
                return daysIntervals[record.data.daysIntervalId].label;
              case "daysOfWeek":
                return [
                  ...(record.data.weekDays.includes("0") ? ["Sunday"] : []),
                  ...(record.data.weekDays.includes("1") ? ["Monday"] : []),
                  ...(record.data.weekDays.includes("2") ? ["Tuesday"] : []),
                  ...(record.data.weekDays.includes("3") ? ["Wednesday"] : []),
                  ...(record.data.weekDays.includes("4") ? ["Thursday"] : []),
                  ...(record.data.weekDays.includes("5") ? ["Friday"] : []),
                  ...(record.data.weekDays.includes("6") ? ["Saturday"] : [])
                ].join(", ");
              default:
                return "";
            }
          },
          sortable: false
        },
        {
          text: "Instructions",
          field: "instructions",
          editor: false,
          type: "template",
          width: 450,
          template: ({ record }) =>
            `<div class="text-wrap">${
              record.instructions ? record.instructions : ""
            }</div>`,
          sortable: false
        },
        {
          text: "",
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: async ({ record }) => {
                this.formScheduledId = record.data.id;
                this.formScheduled = {
                  ...record.data
                };
                this.clearErrors();
                this.$refs["form-scheduled-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2 mr-1" src="${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    this.form.scheduledMedications.splice(
                      record.data.parentIndex,
                      1
                    );
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ];
    },
    prnItems() {
      return this.form.prnMedications.map(item => {
        let rowHeight = 50;
        const singleLineHeight = 25;
        const charsInstructionsPerLine = 80;
        const maxFactor = Math.max(
          Math.ceil(item.instructions.length / charsInstructionsPerLine)
        );
        if (maxFactor > 1) rowHeight += singleLineHeight * (maxFactor - 1);
        return {
          ...item,
          rowHeight: rowHeight,
          inputRows: maxFactor
        };
      });
    },
    prnColumns() {
      return [
        {
          text: "Range",
          width: 170,
          editor: false,
          type: "template",
          template: data => {
            const displayFromDay = Number.isInteger(
              tryParseInt(data.record.data.fromDay, null)
            );
            const displayToDay = Number.isInteger(
              tryParseInt(data.record.data.toDay, null)
            );

            return `
            ${displayFromDay ? `From: Day ${data.record.data.fromDay}` : ""}
            ${displayFromDay && displayToDay ? " - " : ""}
            ${displayToDay ? `To: Day ${data.record.data.toDay}` : ""}`;
          }
        },
        {
          text: "Medication",
          field: "medication",
          flex: 1,
          editor: false,
          type: "template",
          template: data => {
            return `<div class="text-wrap">${data.record.data.medication.label}, ${data.record.data.dosage} ${data.record.data.measurementUnit.label} by ${data.record.data.route.label}</div>`;
          },
          sortable: false
        },
        {
          text: "Instructions",
          field: "instructions",
          editor: false,
          type: "template",
          width: 450,
          template: ({ record }) =>
            `<div class="text-wrap">${
              record.instructions ? record.instructions : ""
            }</div>`,
          sortable: false
        },
        {
          text: "Maximum Dosage",
          field: "maximumDosagePerDay",
          editor: false,
          type: "template",
          width: 450,
          template: ({ record }) =>
            record.maximumDosagePerDay ? record.maximumDosagePerDay : "",
          sortable: false
        },
        {
          text: "",
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: async ({ record }) => {
                this.formPrnId = record.data.id;
                this.formPrn = {
                  ...record.data
                };
                this.clearErrors();
                this.$refs["form-prn-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2 mr-1" src="${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    this.form.prnMedications.splice(record.data.parentIndex, 1);
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ];
    },
    frequencyOptions() {
      return Object.values(frequencies).map(f => ({
        value: f.key,
        label: f.label
      }));
    },
    scheduledFrequency: {
      get() {
        return this.formScheduled.frequencyId
          ? this.frequencyOptions.find(
              frequency => frequency.value == this.formScheduled.frequencyId
            )
          : null;
      },
      set(newValue) {
        this.formScheduled.frequencyId = newValue ? newValue.value : null;
      }
    },
    daysIntervalOptions() {
      return Object.values(daysIntervals).map(d => ({
        value: d.key,
        label: d.label
      }));
    },
    daysInterval: {
      get() {
        return this.formScheduled.daysIntervalId
          ? this.daysIntervalOptions.find(
              daysInterval =>
                daysInterval.value == this.formScheduled.daysIntervalId
            )
          : null;
      },
      set(newValue) {
        this.formScheduled.daysIntervalId = newValue ? newValue.value : null;
      }
    },
    timesPerDayOptions() {
      return Object.values(timesPerDayOptions).map(t => ({
        value: t.key,
        label: t.label
      }));
    },
    timesPerDay: {
      get() {
        return this.formScheduled.timesPerDayId
          ? this.timesPerDayOptions.find(
              timesPerDay =>
                timesPerDay.value == this.formScheduled.timesPerDayId
            )
          : null;
      },
      set(newValue) {
        this.formScheduled.timesPerDayId = newValue ? newValue.value : null;
        this.initializeTimes();
      }
    },
    timeOptions() {
      return timeOptions.map(t => ({
        value: t,
        text: formatTimeOnly(t, "HH:mm")
      }));
    }
  }
};
</script>
<style scoped>
.times-container {
  height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
